import { ArrowRightIcon, CalendarClockIcon, CheckIcon, MessageSquareIcon, PhoneIcon, SendIcon, SettingsIcon, UploadIcon, UserPlusIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { CopyButton } from '@/components/CopyButton';
import { DeliveryBar, deliverySummary } from '@/components/DeliveryBar';
import { EmptyState } from '@/components/EmptyState';
import { SectionTitle } from '@/components/PageHeader';
import { MessageStatusBadge } from '@/components/StatusBadge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useContactStats } from '@/features/contacts/queries';
import { useMessages } from '@/features/history/queries';
import { Sparkline } from '@/features/org/overview/charts';
import { Delta } from '@/features/org/overview/KpiBand';
import { providerLabel } from '@/features/history/RecipientPage';
import { useJobWeather } from '@/features/automation/queries';
import { WeatherAlertList } from '@/features/automation/WeatherAlerts';
import { useMembers } from '@/features/team/queries';
import { TokenText } from '@/features/templates/TokenText';
import { useJobContext } from '@/layouts/JobLayout';
import { useMe } from '@/lib/auth';
import { fmtDateTime, fmtDateTimeInZone, fmtNumber, fmtPercent, fmtRelative, tzAbbreviation } from '@/lib/format';
import type { MessageDto } from '@/lib/types';
import { cn, initials } from '@/lib/utils';
import { useJobDailyRange, useJobSummary } from './queries';

/**
 * Job home. What to do next (compose, import, invite) sits first; then the four numbers that matter, each
 * for the last 30 days against the 30 before with a sparkline; then deliveries by day beside what is coming
 * up (or the setup checklist on a new job), the sending number and the team; then the most recent messages.
 */
export function JobOverviewPage() {
  const { job, permissions } = useJobContext();
  const me = useMe();
  const summary = useJobSummary(job.id);
  const daily = useJobDailyRange(job.id, 60);
  const stats = useContactStats(job.id);
  const members = useMembers(job.id);
  const recent = useMessages(job.id, { page: 1, pageSize: 5, view: 'sent' });
  const upcoming = useMessages(job.id, { page: 1, pageSize: 4, view: 'upcoming' });
  const tz = job.timezone ?? me.tenant.quietHours.timezone;
  // Only a job with a geocoded site ever asks for a forecast; the server caches it per site.
  const weather = useJobWeather(job.id, me.tenant.features.weatherAutomation && job.latitude !== null && job.longitude !== null);
  const alerts = weather.data?.forecast?.alerts ?? [];

  const s = summary.data?.summary;
  const series60 = daily.data?.series ?? [];
  const last30 = series60.slice(-30);
  const prev30 = series60.slice(-60, -30);
  const sum = (rows: typeof series60, key: 'messages' | 'recipients' | 'delivered' | 'failed') => rows.reduce((n, d) => n + d[key], 0);
  const cur = { messages: sum(last30, 'messages'), recipients: sum(last30, 'recipients'), delivered: sum(last30, 'delivered'), failed: sum(last30, 'failed') };
  const prev = { messages: sum(prev30, 'messages'), recipients: sum(prev30, 'recipients'), delivered: sum(prev30, 'delivered'), failed: sum(prev30, 'failed') };
  const rate = (x: { delivered: number; failed: number }) => (x.delivered + x.failed ? (x.delivered / (x.delivered + x.failed)) * 100 : 0);
  const series = last30.map((d) => ({ ...d, label: shortDay(d.day) }));
  const hasActivity = series.some((d) => d.recipients > 0 || d.messages > 0);
  const reachable = stats.data ? Math.max(0, stats.data.total - stats.data.optedOut) : undefined;
  const team = members.data?.items ?? [];
  const admins = team.filter((m) => m.role === 'admin').length;

  const setup = [
    { key: 'number', label: 'Phone number assigned', done: !!job.phoneNumber, hint: job.phoneNumber ? `Sends from ${job.phoneNumber.display}` : permissions.assignPhoneNumber ? 'Pick a number in Settings' : 'An organization admin assigns one', to: `/jobs/${job.id}/settings`, icon: PhoneIcon },
    { key: 'contacts', label: 'Contacts loaded', done: (stats.data?.total ?? 0) > 0, hint: stats.data && stats.data.total > 0 ? `${fmtNumber(stats.data.total)} on the list` : 'Import a CSV or add people one by one', to: `/jobs/${job.id}/contacts${(stats.data?.total ?? 0) > 0 ? '' : '?import=1'}`, icon: UploadIcon },
    { key: 'team', label: 'Team invited', done: team.length > 1, hint: team.length > 1 ? `${fmtNumber(team.length)} people on this job` : 'Add the people who run this job', to: `/jobs/${job.id}/team`, icon: UserPlusIcon },
    { key: 'first', label: 'First message sent', done: (s?.messages ?? 0) > 0, hint: (s?.messages ?? 0) > 0 ? `${fmtNumber(s?.messages)} sent so far` : permissions.send ? 'Compose once contacts are in' : 'A job admin sends the messages', to: `/jobs/${job.id}/compose`, icon: SendIcon },
  ];
  const setupLoading = stats.isPending || members.isPending || summary.isPending;
  const setupDone = setup.every((x) => x.done);
  const canCompose = permissions.send && !!job.phoneNumber && (reachable ?? 0) > 0 && job.status === 'active';
  const upcomingItems = upcoming.data?.items ?? [];

  return (
    <div className="space-y-5 sm:space-y-6">
      {alerts.length > 0 && (
        <Link to={`/jobs/${job.id}/automation`} className="block" aria-label="Weather alerts for this site">
          <WeatherAlertList compact alerts={alerts} tz={tz} />
        </Link>
      )}
      {/* ------------------------------------------------------------ the four numbers */}
      <div className="bg-border grid grid-cols-2 gap-px border lg:grid-cols-4" role="list" aria-label="Job at a glance">
        <Tile label="Reachable contacts" value={reachable === undefined ? undefined : fmtNumber(reachable)} loading={stats.isPending} hint={stats.data ? `${fmtNumber(stats.data.optedOut)} opted out · ${fmtNumber(stats.data.total)} total` : undefined} />
        <Tile label="Composed, 30 days" value={daily.data ? fmtNumber(cur.messages) : undefined} loading={daily.isPending} delta={daily.data ? <Delta current={cur.messages} previous={prev.messages} previousLabel="the 30 days before" /> : undefined} spark={daily.data ? last30.map((d) => d.messages) : undefined} hint={s ? `${fmtNumber(s.messages)} all time · ${fmtNumber(cur.recipients)} people reached` : undefined} />
        <Tile
          label="Delivery rate, 30 days"
          value={daily.data ? (cur.delivered + cur.failed ? fmtPercent(rate(cur)) : '—') : undefined}
          loading={daily.isPending}
          delta={daily.data && cur.delivered + cur.failed > 0 ? prev.delivered + prev.failed > 0 ? <Delta current={rate(cur)} previous={rate(prev)} percentPoints previousLabel="the 30 days before" /> : <span className="text-muted-foreground text-xs">nothing sent in the 30 days before</span> : undefined}
          spark={daily.data ? last30.map((d) => (d.delivered + d.failed ? (d.delivered / (d.delivered + d.failed)) * 100 : 0)) : undefined}
          hint={daily.data ? `${fmtNumber(cur.delivered)} delivered · ${fmtNumber(cur.failed)} failed` : undefined}
        />
        <Tile label="Team" value={members.data ? fmtNumber(team.length) : undefined} loading={members.isPending} hint={members.data ? `${fmtNumber(admins)} ${admins === 1 ? 'admin can' : 'admins can'} send` : undefined} />
      </div>

      {/* ------------------------------------------------------------ deliveries + side column */}
      <div className="grid gap-4 lg:grid-cols-3 lg:items-start">
        <Card
          title="Deliveries, last 30 days"
          meta={daily.isPending ? 'Loading…' : hasActivity ? 'Every text attempted, by day and outcome.' : 'Nothing sent in this window.'}
          className="lg:col-span-2"
          action={
            daily.data && hasActivity ? (
              <dl className="hidden shrink-0 items-baseline gap-5 sm:flex">
                <Total value={fmtNumber(cur.delivered)} label="delivered" />
                <Total value={fmtNumber(cur.failed)} label="failed" bad={cur.failed > 0} />
                {(s?.pending ?? 0) > 0 && <Total value={fmtNumber(s?.pending)} label="in flight" />}
              </dl>
            ) : undefined
          }
        >
          {daily.isPending ? (
            <Skeleton className="h-52 w-full sm:h-60" />
          ) : !hasActivity ? (
            <div className="flex h-52 flex-col items-center justify-center text-center sm:h-60">
              <MessageSquareIcon className="text-muted-foreground mb-3 size-6" aria-hidden />
              <p className="text-sm font-medium">No messages in the last 30 days</p>
              <p className="text-muted-foreground mt-1 max-w-xs text-xs leading-relaxed">{canCompose ? 'Your contacts are in and the number is live. Send the first one.' : 'Deliveries show up here day by day once messages go out.'}</p>
              {canCompose && (
                <Button asChild size="sm" className="mt-4">
                  <Link to={`/jobs/${job.id}/compose`}>
                    <SendIcon /> Compose a message
                  </Link>
                </Button>
              )}
            </div>
          ) : (
            <>
              <Legend />
              <DeliveryChart series={series} />
            </>
          )}
        </Card>

        <aside className="grid gap-4">
          {setupLoading ? (
            <Skeleton className="h-56 w-full" />
          ) : !setupDone ? (
            <Card title="Set up this job" meta={`${setup.filter((x) => x.done).length} of ${setup.length} done`} padded={false}>
              <ol className="divide-y">
                {setup.map((step, i) => (
                  <li key={step.key}>
                    <Link to={step.to} className="hover:bg-accent/50 group flex items-start gap-3 px-5 py-3 transition-colors">
                      <span className={cn('mt-0.5 flex size-6 shrink-0 items-center justify-center border text-[11px] font-medium tabular-nums', step.done ? 'border-primary bg-primary text-primary-foreground' : 'text-muted-foreground')} aria-hidden>
                        {step.done ? <CheckIcon className="size-3.5" /> : i + 1}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className={cn('block text-sm', step.done ? 'text-muted-foreground line-through decoration-muted-foreground/50' : 'font-medium')}>{step.label}</span>
                        <span className="text-muted-foreground block text-xs">{step.hint}</span>
                      </span>
                      {!step.done && <ArrowRightIcon className="text-muted-foreground mt-1 size-4 shrink-0 opacity-0 transition-opacity group-hover:opacity-100" aria-hidden />}
                    </Link>
                  </li>
                ))}
              </ol>
            </Card>
          ) : (
            <Card
              title="Coming up"
              meta={upcoming.isPending ? 'Loading…' : upcomingItems.length ? `${fmtNumber(upcomingItems.length)} scheduled or drafted` : 'Nothing scheduled.'}
              padded={false}
              action={
                <Link to={`/jobs/${job.id}/history?view=upcoming`} className="text-muted-foreground hover:text-foreground text-xs underline-offset-4 hover:underline">
                  All
                </Link>
              }
            >
              {upcoming.isPending ? (
                <div className="p-5">
                  <Skeleton className="h-16 w-full" />
                </div>
              ) : upcomingItems.length === 0 ? (
                <div className="px-5 pb-5 text-xs leading-relaxed">
                  <p className="text-muted-foreground">Schedule a reminder from Compose, or let an automation rule draft one, and it shows up here.</p>
                  {permissions.send && (
                    <Button asChild variant="outline" size="sm" className="mt-3">
                      <Link to={`/jobs/${job.id}/compose`}>
                        <CalendarClockIcon /> Schedule a message
                      </Link>
                    </Button>
                  )}
                </div>
              ) : (
                <ul className="divide-y">
                  {upcomingItems.map((m) => (
                    <li key={m.id}>
                      <Link to={`/jobs/${job.id}/history/${m.id}`} className="hover:bg-accent/50 grid gap-1 px-5 py-3 transition-colors">
                        <span className="flex items-center justify-between gap-2">
                          <span className="text-xs font-medium tabular-nums">{m.scheduledAt ? `${fmtDateTimeInZone(m.scheduledAt, tz)} ${tzAbbreviation(tz)}` : `Drafted ${fmtRelative(m.createdAt)}${m.createdBy ? ` by ${m.createdBy.name.split(' ')[0]}` : ' by automation'}`}</span>
                          <MessageStatusBadge status={m.status} />
                        </span>
                        <span className="text-muted-foreground line-clamp-2 text-xs leading-relaxed">{m.body}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          )}

          <Card title="Sending number" padded>
            {job.phoneNumber ? (
              <div className="grid gap-2">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-lg font-semibold tracking-tight tabular-nums">{job.phoneNumber.display}</span>
                  <CopyButton value={job.phoneNumber.e164} label="Copy" />
                </div>
                <p className="text-muted-foreground text-xs leading-relaxed">
                  {job.senderLabel ? (
                    <>
                      Texts start with <span className="text-foreground font-medium">“{job.senderLabel}:”</span>.{' '}
                    </>
                  ) : null}
                  STOP replies are honored automatically.
                </p>
              </div>
            ) : (
              <div className="grid gap-3">
                <p className="text-destructive text-sm">No phone number yet, so nothing can be sent.</p>
                {permissions.assignPhoneNumber ? (
                  <Button asChild variant="outline" size="sm" className="justify-start">
                    <Link to={`/jobs/${job.id}/settings`}>
                      <SettingsIcon /> Assign a number in Settings
                    </Link>
                  </Button>
                ) : (
                  <p className="text-muted-foreground text-xs">Ask an organization admin to assign one.</p>
                )}
              </div>
            )}
          </Card>

          <Card
            title="Team"
            meta={members.data ? `${fmtNumber(team.length)} ${team.length === 1 ? 'person' : 'people'} · ${fmtNumber(admins)} ${admins === 1 ? 'admin' : 'admins'}` : undefined}
            padded
            action={
              <Link to={`/jobs/${job.id}/team`} className="text-muted-foreground hover:text-foreground text-xs underline-offset-4 hover:underline">
                Manage
              </Link>
            }
          >
            {members.isPending ? (
              <Skeleton className="h-9 w-40" />
            ) : team.length === 0 ? (
              <p className="text-muted-foreground text-xs">Nobody is on this job yet.</p>
            ) : (
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex -space-x-2">
                  {team.slice(0, 6).map((m) => (
                    <Avatar key={m.id} className="ring-card size-8 ring-2" title={`${m.fullName} · ${m.role === 'admin' ? 'Admin' : 'User'}`}>
                      <AvatarFallback className="text-[11px]">{initials(m.fullName)}</AvatarFallback>
                    </Avatar>
                  ))}
                  {team.length > 6 && <span className="bg-muted text-muted-foreground ring-card flex size-8 items-center justify-center text-[11px] ring-2">+{team.length - 6}</span>}
                </div>
                <p className="text-muted-foreground min-w-0 flex-1 truncate text-xs">
                  {team
                    .slice(0, 3)
                    .map((m) => m.fullName.split(' ')[0])
                    .join(', ')}
                  {team.length > 3 ? ` and ${team.length - 3} more` : ''}
                </p>
              </div>
            )}
          </Card>
        </aside>
      </div>

      {/* ------------------------------------------------------------ recent */}
      <section>
        <SectionTitle
          className="mb-3"
          actions={
            <Button asChild variant="ghost" size="sm">
              <Link to={`/jobs/${job.id}/history`}>View all</Link>
            </Button>
          }
        >
          Recent messages
        </SectionTitle>
        {recent.isPending ? (
          <Skeleton className="h-32 w-full" />
        ) : (recent.data?.items.length ?? 0) === 0 ? (
          <EmptyState icon={MessageSquareIcon} title="No messages yet" description="Messages sent to this job's contacts will appear here with delivery status." className="py-10" />
        ) : (
          <div className="bg-card divide-y border">
            {recent.data?.items.map((m) => (
              <RecentRow key={m.id} jobId={job.id} m={m} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

// ---------------------------------------------------------------- pieces

function Tile({ label, value, hint, loading, delta, spark }: { label: string; value: string | undefined; hint?: string; loading?: boolean; delta?: ReactNode; spark?: number[] }) {
  return (
    <div role="listitem" className="bg-card flex min-w-0 flex-col gap-1.5 p-4 sm:p-5">
      <span className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">{label}</span>
      {loading || value === undefined ? <Skeleton className="h-9 w-16" /> : <span className="font-display text-3xl leading-none tracking-tight sm:text-4xl">{value}</span>}
      {delta}
      {spark && <Sparkline points={spark} className="mt-1" />}
      {hint && <span className="text-muted-foreground mt-auto truncate text-xs">{hint}</span>}
    </div>
  );
}

function Total({ value, label, bad }: { value: string; label: string; bad?: boolean }) {
  return (
    <div className="text-right">
      <dd className={cn('text-lg leading-none font-semibold tracking-tight tabular-nums', bad && 'text-destructive')}>{value}</dd>
      <dt className="text-muted-foreground mt-0.5 text-[11px]">{label}</dt>
    </div>
  );
}

function Card({ title, meta, action, children, className, padded = true }: { title: string; meta?: string; action?: ReactNode; children: ReactNode; className?: string; padded?: boolean }) {
  return (
    <section className={cn('bg-card flex min-w-0 flex-col border', className)} aria-label={title}>
      <div className={cn('flex items-start justify-between gap-3 px-5 pt-4', padded ? 'mb-3' : 'mb-2')}>
        <div className="min-w-0">
          <h2 className="text-sm font-semibold tracking-tight">{title}</h2>
          {meta && <p className="text-muted-foreground mt-0.5 text-xs">{meta}</p>}
        </div>
        {action}
      </div>
      <div className={cn('min-w-0 flex-1', padded && 'px-5 pb-5')}>{children}</div>
    </section>
  );
}

function Legend() {
  return (
    <ul className="text-muted-foreground mb-3 flex items-center gap-4 text-xs" aria-label="Legend">
      <li className="flex items-center gap-1.5">
        <span className="inline-block size-2.5" style={{ background: 'var(--chart-1)' }} aria-hidden /> Delivered
      </li>
      <li className="flex items-center gap-1.5">
        <span className="inline-block size-2.5 border" style={{ background: 'repeating-linear-gradient(45deg, var(--destructive) 0 1.5px, transparent 1.5px 3.5px)', borderColor: 'var(--destructive)' }} aria-hidden /> Failed
      </li>
    </ul>
  );
}

/**
 * Delivered stacks under failed per day. Delivered wears the tenant primary (--chart-1); failed is the
 * destructive colour with a hatch texture so the pair never relies on colour alone. A 2px card-coloured
 * stroke keeps a gap between stacked segments and neighbouring bars.
 */
function DeliveryChart({ series }: { series: Array<{ label: string; day: string; delivered: number; failed: number; messages: number }> }) {
  return (
    <div className="h-52 sm:h-60">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={series} margin={{ top: 8, right: 4, bottom: 0, left: -16 }} barCategoryGap="30%" maxBarSize={24}>
          <defs>
            <pattern id="hatch-failed" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
              <rect width="6" height="6" fill="var(--card)" />
              <line x1="0" y1="0" x2="0" y2="6" stroke="var(--destructive)" strokeWidth="2.5" />
            </pattern>
          </defs>
          <CartesianGrid vertical={false} stroke="var(--border)" />
          <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} interval={4} />
          <YAxis tickLine={false} axisLine={false} allowDecimals={false} width={40} tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} />
          <Tooltip cursor={{ fill: 'var(--accent)', opacity: 0.6 }} content={(props) => <ChartTip active={props.active} payload={props.payload} />} />
          <Bar dataKey="delivered" stackId="a" fill="var(--chart-1)" stroke="var(--card)" strokeWidth={2} name="Delivered" isAnimationActive={false} />
          <Bar dataKey="failed" stackId="a" fill="url(#hatch-failed)" stroke="var(--card)" strokeWidth={2} name="Failed" isAnimationActive={false} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function ChartTip({ active, payload }: { active?: boolean; payload?: ReadonlyArray<{ payload?: unknown }> }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload as { day: string; delivered: number; failed: number; messages: number } | undefined;
  if (!row) return null;
  return (
    <div className="bg-popover text-popover-foreground border px-3 py-2 text-xs shadow-sm">
      <div className="text-muted-foreground mb-1">{longDay(row.day)}</div>
      <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-0.5 tabular-nums">
        <span>Delivered</span>
        <span className="text-right font-medium">{fmtNumber(row.delivered)}</span>
        <span>Failed</span>
        <span className="text-right font-medium">{fmtNumber(row.failed)}</span>
        <span className="text-muted-foreground">Messages</span>
        <span className="text-muted-foreground text-right">{fmtNumber(row.messages)}</span>
      </div>
    </div>
  );
}

function RecentRow({ jobId, m }: { jobId: string; m: MessageDto }) {
  return (
    <Link to={`/jobs/${jobId}/history/${m.id}`} className="hover:bg-accent/50 grid gap-2 px-4 py-3 transition-colors sm:grid-cols-[1fr_180px_auto] sm:items-center sm:gap-3 sm:px-5">
      <div className="min-w-0">
        <p className="truncate text-sm" title={m.renderedBody}>
          <TokenText body={m.renderedBody} className="whitespace-nowrap" />
        </p>
        <p className="text-muted-foreground mt-0.5 text-xs">
          <span title={fmtDateTime(m.createdAt)}>{fmtRelative(m.createdAt)}</span> · {m.createdBy?.name ?? 'Automation'} · {fmtNumber(m.recipientCount)} {m.recipientCount === 1 ? 'recipient' : 'recipients'}
        </p>
      </div>
      <div className="flex items-center gap-3 sm:contents">
        <div className="grid min-w-0 flex-1 gap-1">
          <DeliveryBar counts={m.counts} total={m.recipientCount} />
          <span className="text-muted-foreground truncate text-[11px] tabular-nums">
            {deliverySummary(m.counts, { short: true })}
            {m.provider ? ` · via ${providerLabel(m.provider)}` : ''}
          </span>
        </div>
        <div className="shrink-0 sm:justify-self-end">
          <MessageStatusBadge status={m.status} />
        </div>
      </div>
    </Link>
  );
}

function shortDay(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime()) ? iso : new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(d);
}
function longDay(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime()) ? iso : new Intl.DateTimeFormat(undefined, { weekday: 'short', month: 'long', day: 'numeric' }).format(d);
}
